import { Button, Card } from "@/ingot";
import { useEffect, useMemo, useRef, useState } from "react";
import type { TFunction } from "i18next";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { computeEffectiveDays } from "@/api/types";
import type {
  Address,
  CheckoutPickup,
  CheckoutPrefillAddress,
} from "@/api/types";
import {
  useAresLookup,
  useAuthMe,
  useCart,
  useCatalog,
  useCheckout,
  useCheckoutPaymentMethods,
  useCheckoutPrefill,
} from "@/api/queries";
import {
  useCartDeliveryLadderOptions,
  useCheckoutCarriers,
  useCheckoutPickup,
  useCheckoutShippingAvailability,
  useCheckoutTotalsPreview,
  type CartDeliveryLadderOption,
} from "@/api/queries/cart";
import { useTenantBranding } from "@/api/queries/branding";
import {
  useCheckoutDefaultCountry,
  useDemoCheckoutEnd,
} from "@/api/queries/checkout";
import { useIsCurrentOrgAdmin } from "@/api/queries/orgs";
import { useBusinessInfo } from "@/api/queries/businessInfo";
import { useCheckoutDeliveryCountries } from "@/api/queries/checkout/deliveryCountries";
import { pickI18nBlob, pickI18nText } from "@/components/admin/I18nField";
import { fallbackRungLabel } from "@/lib/ladderFallback";
import { checkoutPrefillNote } from "@/lib/checkoutPrefillNote";
import { EmptyCartUpload } from "@/components/cart/EmptyCartUpload";
import { ShippingDoesNotFitGate } from "@/components/cart/ShippingDoesNotFitGate";
import { CountrySelect } from "@/components/forms/CountrySelect";
import { PhoneInput } from "@/components/forms/PhoneInput";

import { Pill } from "@/components/ui/Pill";
import { SubmitBlockers, useSubmitGuard } from "@/components/ui/SubmitGuard";
import { VatDisplayToggle } from "@/components/VatDisplayToggle";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { postalCodeBeforeCity, postalCodeMatches } from "@/lib/addressFormat";
import { ApiError } from "@/lib/api";
import { currentQuoteCheckout } from "@/lib/quoteCheckout";
import { displayId } from "@/lib/identifiers";
import {
  companyRegistryFor,
  isValidCompanyId,
  isValidVatId,
} from "@/lib/companyIds";
import {
  clearCheckoutDraft,
  readCheckoutDraft,
  writeCheckoutDraft,
} from "@/lib/checkoutDraft";
import { deliveryDateFor, formatDeliveryDate } from "@/lib/deliveryDate";
import { pendingDateKey } from "@/lib/deliveryPending";
import { useSeo } from "@/lib/seo";
import { useProcessTypeLabel } from "@/lib/useProcessTypeLabel";
import { majorToMinor, minorFromPair, minorToMajor } from "@/lib/money";
import { formatWeeklySchedule } from "@/lib/weeklySchedule";
import { formatCount, formatKgAmount } from "@/lib/weight";
import { orderSummaryTotals } from "@/lib/orderSummaryTotals";
import { cx, formatMoney, formatVatRate } from "@/lib/utils";
import {
  defaultVatDisplayMode,
  storefrontVatDisplay,
  writeVatDisplayPreference,
  type VatDisplayMode,
} from "@/lib/vatDisplay";

/** Hodnota osobního odběru ve vybírátku doručení (KAN-1605). Kód dopravce
 *  ``@`` obsahovat nesmí (``^[a-z][a-z0-9_]*$``), takže se s ním nesrazí. */
const PICKUP_OPTION = "@pickup";

const _EMPTY_ADDR: Address = {
  first_name: "",
  last_name: "",
  street: "",
  city: "",
  postal_code: "",
  // KAN-1928 — žádná země natvrdo: výchozí přijde z
  // ``GET /checkout/default-country`` (IP zákazníka, pokud tam obchod
  // doručuje, jinak první doručovací země obchodu).
  country_code: "",
};

type Step = "address" | "delivery";

/** Co pokladna drží jako rozpracovaný koncept (KAN-1428). */
interface CheckoutDraft {
  shipping: Address;
  billingSame: boolean;
  billing: Address;
  company: { ico: string; dic: string };
  customerReference: string;
  /** KAN-1913 — zemi zvolil zákazník sám (ne výchozí hodnota formuláře). */
  countryTouched?: CountryTouched;
}

/** KAN-1913 — které výběry země změnil zákazník ručně. Výchozí země
 *  formuláře se jako „vyplněná" nepočítá: prefill ji přepíše. */
interface CountryTouched {
  shipping: boolean;
  billing: boolean;
}

/** Starý koncept (před KAN-1913) příznak nenese — za ručně zvolenou se
 *  pak bere jakákoli vyplněná země (formulář od KAN-1928 začíná bez ní). */
function draftCountryTouched(
  addr: Partial<Address> | undefined,
  flag: boolean | undefined,
): boolean {
  if (typeof flag === "boolean") return flag;
  const code = addr?.country_code;
  return !!code && code !== _EMPTY_ADDR.country_code;
}

/**
 * Two-step checkout (visual redesign for Project Steel; flow
 * unchanged).
 *
 *   Step 1 — "adresa":  shipping + optional billing + company / ARES.
 *   Step 2 — "doprava": delivery tier + carrier + live price preview +
 *                       the actual Place Order submit.
 *
 * The two-step split was a deliberate UX choice (see history before
 * 2026-04-26): customers were skimming the address block and picking
 * `express` without absorbing the price impact, so isolating the
 * tier choice on its own page makes it deliberate.
 *
 * Live price preview on step 2: we don't call the BE. Delivery
 * multiplier applies uniformly to ``unit_price_raw`` so the new
 * subtotal is ``cart.subtotal_czk × new_multiplier / old_multiplier``.
 * Cart is always at ``standard`` (multiplier 1.0) since the
 * configurator doesn't offer a per-line choice anymore.
 */
export default function Checkout(): JSX.Element {
  const { t: tCo } = useTranslation("checkout");
  const { t: tCart } = useTranslation("cart");
  const formatProcessType = useProcessTypeLabel();
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;
  const locale = lang === "cs" ? "cs-CZ" : "en-US";
  const navigate = useNavigate();

  // KAN-1658 — objednávka z vystavené nabídky: táž pokladna, zdrojem
  // položek je nabídka (``lib/quoteCheckout.ts``), cena je zamčená.
  const quoteCheckout = currentQuoteCheckout();
  const cart = useCart(quoteCheckout ? { retry: false } : undefined);
  const checkout = useCheckout({
    onSuccess: (res) => {
      // KAN-2093 — košík a souhrn uklízí ``useCheckout`` (i koncept);
      // mazání tady zůstává pro případ, že by se hook někdy vyměnil.
      clearCheckoutDraft();
      // KAN-1644 — navenek jen veřejné číslo objednávky.
      navigate(`/${lang}/order/${displayId(res)}`);
    },
  });

  // KAN-35 — na ukázkovém obchodě platformy „Zaplatit" objednávku
  // nezakládá: server zapíše CRM signál a návštěvník jde na nabídku
  // vlastního obchodu. Rozhoduje příznak z brandingu, nikdy slug.
  const demoEnd = useDemoCheckoutEnd({
    onSuccess: () => navigate(`/${lang}/demo-shop`),
  });

  useSeo({ title: tCo("title"), path: "/checkout", lang });

  const [step, setStep] = useState<Step>("address");
  // KAN-1428 — koncept z předchozí návštěvy pokladny v tomhle panelu
  // (zákazník odběhl do košíku). Čte se jednou; prefill z účtu pak
  // doplní jen pole, která zůstala prázdná.
  const [draft] = useState(() => readCheckoutDraft<CheckoutDraft>());
  const [shipping, setShipping] = useState<Address>(() => ({
    ..._EMPTY_ADDR,
    ...draft?.shipping,
  }));
  const [billingSame, setBillingSame] = useState(draft?.billingSame ?? true);
  const [billing, setBilling] = useState<Address>(() => ({
    ..._EMPTY_ADDR,
    ...draft?.billing,
  }));
  const [company, setCompany] = useState(() => ({
    ico: draft?.company?.ico ?? "",
    dic: draft?.company?.dic ?? "",
  }));
  // KAN-1913 — ref, ne state: čte ho jednorázový prefill a zapisuje se
  // do konceptu spolu s adresou, re-render kvůli němu není potřeba.
  const countryTouched = useRef<CountryTouched>({
    shipping: draftCountryTouched(
      draft?.shipping,
      draft?.countryTouched?.shipping,
    ),
    billing: draftCountryTouched(draft?.billing, draft?.countryTouched?.billing),
  });
  const onShippingChange = (next: Address): void => {
    if (next.country_code !== shipping.country_code) {
      countryTouched.current.shipping = true;
    }
    setShipping(applyEdit(shipping, next));
  };
  const onBillingChange = (next: Address): void => {
    if (next.country_code !== billing.country_code) {
      countryTouched.current.billing = true;
    }
    setBilling(applyEdit(billing, next));
  };
  // KAN-1928 — výchozí země (IP → první doručovací země) jen DOPLNÍ
  // prázdnou zemi. Ruční volbu ani prefill z minulé objednávky nepřepíše:
  // ty zemi vyplní dřív, nebo (prefill) přepíší tuhle, protože ji
  // nepovažují za zvolenou (``countryTouched`` zůstává ``false``).
  const defaultCountry = useCheckoutDefaultCountry().data?.country_code;
  useEffect(() => {
    if (!defaultCountry) return;
    const fill = (prev: Address): Address =>
      prev.country_code ? prev : { ...prev, country_code: defaultCountry };
    setShipping(fill);
    setBilling(fill);
  }, [defaultCountry]);
  // KAN-992 — číslo objednávky, které si vystavil odběratel. Prázdné je
  // v pořádku: pole je volitelné i pro firemní účet.
  const [customerReference, setCustomerReference] = useState(
    draft?.customerReference ?? "",
  );
  useEffect(() => {
    // KAN-2093 — po odeslané objednávce se koncept už nezapisuje: smazal
    // ho ``useCheckout`` a nový zápis by ho do dalšího nákupu vrátil.
    if (checkout.isSuccess) return;
    writeCheckoutDraft({
      shipping,
      billingSame,
      billing,
      company,
      customerReference,
      countryTouched: { ...countryTouched.current },
    } satisfies CheckoutDraft);
  }, [shipping, billingSame, billing, company, customerReference, checkout.isSuccess]);
  const [carrier, setCarrier] = useState<string>("");
  // Výchozí NEzaškrtnuto. Předzaškrtnutý souhlas není souhlas — musí to
  // být výslovný úkon kupujícího, jinak nemá cenu ho ani ukládat.
  const [termsAccepted, setTermsAccepted] = useState(false);
  // KAN-748 — testovací objednávka; checkbox vidí jen přihlášený admin
  // vlastního tenanta (BE příznak od kohokoli jiného tiše ignoruje).
  const [isTestOrder, setIsTestOrder] = useState(false);
  const guardAres = useSubmitGuard();
  const guardContinue = useSubmitGuard();
  const [deliveryCode, setDeliveryCode] = useState<string>("standard");
  const catalog = useCatalog(lang);
  const branding = useTenantBranding();
  // Once step 1 has a country we ask the BE to price each carrier for
  // the cart and drop carriers that can't deliver there. Without
  // a country we still render the unpriced list so the dropdown
  // appears as soon as the user hits step 2.
  const carriers = useCheckoutCarriers(shipping.country_code || undefined);
  // KAN-1754 — the parts fit into no shipping band to this country: no
  // carrier is offered and the checkout leads to a price enquiry instead.
  const shippingAvailability = useCheckoutShippingAvailability(
    shipping.country_code || undefined,
  );
  const shippingFitsNowhere = shippingAvailability.data?.fits_nowhere === true;
  // KAN-1605 — osobní odběr je samostatný způsob doručení, ne dopravce.
  // Ve vybírátku je POSLEDNÍ volbou za dopravci; ``null`` = obchod ho
  // nenabízí.
  const pickupQuery = useCheckoutPickup();
  const pickupOffer = pickupQuery.data ?? null;
  const isPickup = carrier === PICKUP_OPTION;
  useEffect(() => {
    // Počkej na seznam dopravců: kdyby odběr dorazil dřív, předvybral by
    // se a po příchodu dopravců by zůstal vybraný — výchozí volba by pak
    // závisela na tom, která odpověď přišla první.
    if (!carriers.isSuccess && !carriers.isError) return;
    const codes = (carriers.data ?? []).map((c) => c.code);
    if (pickupOffer) codes.push(PICKUP_OPTION);
    if (!codes.length) {
      // BE filtered out every carrier (e.g. no rate for this country)
      // — clear any stale selection so the place-order button stays
      // disabled until the user picks a country a carrier supports.
      setCarrier("");
      return;
    }
    setCarrier((prev) => (prev && codes.includes(prev) ? prev : codes[0]));
  }, [carriers.data, carriers.isSuccess, carriers.isError, pickupOffer]);
  // Canonical-ladder picker (PR4+). Empty when the tenant hasn't
  // enabled ladder rungs yet — Checkout then falls back to the legacy
  // ``catalog.delivery_options`` dropdown (the position-based picker
  // from PR3b/PR3c was dropped in PR14 once every tenant on the new
  // model picked through the ladder shape).
  const ladderOptions = useCartDeliveryLadderOptions();
  const hasLadderRungs = (ladderOptions.data?.length ?? 0) > 0;
  const [ladderId, setLadderId] = useState<number | null>(null);
  // Default to the slowest active ladder rung when the picker becomes
  // visible — matches the legacy default of "standard".
  useEffect(() => {
    if (!hasLadderRungs || ladderId !== null) return;
    const sorted = [...(ladderOptions.data ?? [])].sort(
      (a, b) => a.ladder_sort_order - b.ladder_sort_order,
    );
    if (sorted.length > 0) setLadderId(sorted[0].ladder_id);
  }, [hasLadderRungs, ladderId, ladderOptions.data]);
  // KAN-1514 — autoritativní rozpad souhrnu. Tahle obrazovka si ho do
  // KAN-1514 skládala sama a dorovnání minimální objednávky brala
  // z košíku, kde se počítá BEZ dopravy — a dopravu pak přičetla ještě
  // jednou navrch (282 + 119 při minimu 500 dalo 619 místo 500). Server
  // teď vrací řádky, které se sečtou na uvedený celek; nic se tu
  // nedopočítává.
  const totalsPreview = useCheckoutTotalsPreview({
    // KAN-2013 — DPH a celek už od 1. kroku: dokud zákazník zemi nevybral,
    // počítá se se zemí, se kterou počítá košík (``vat_country_code``) —
    // osobní odběr bez vyplněné adresy ukazoval jen mezisoučet a odběr.
    countryCode:
      shipping.country_code || cart.data?.vat_country_code || undefined,
    carrierCode: isPickup ? undefined : carrier || undefined,
    ladderId: hasLadderRungs ? ladderId : null,
    deliveryMethod: isPickup ? "pickup" : "carrier",
    // Jen DIČ ve správném formátu — rozepsané se na server neposílá.
    vatId:
      company.dic.trim() && isValidVatId(company.dic)
        ? company.dic.replace(/\s+/g, "").toUpperCase()
        : undefined,
  });

  const legacyDeliveryOptions = catalog.data?.delivery_options ?? [];
  const hasLegacyDeliveryTiers = legacyDeliveryOptions.length > 0;

  const me = useAuthMe();
  // KAN-748 / KAN-231 — testovací objednávku smí zapnout jen admin
  // TOHOTO tenanta. Ptá se na členství, stejně jako API
  // (``_is_tenant_admin``), ne na globální roli účtu.
  const { isAdmin: isTenantAdmin } = useIsCurrentOrgAdmin();
  useEffect(() => {
    if (!me.data?.email) return;
    // KAN-2111 — u nabídky je odběratel ten z nabídky (předvyplní ho
    // prefill níž); e-mail přihlášeného účtu by ho přebil.
    if (quoteCheckout) return;
    setShipping((prev) =>
      prev.email ? prev : { ...prev, email: me.data!.email },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.data?.email]);

  // VAT display mode — viz Cart.tsx pro pravidla. Logged-in user
  // s ``company`` na profilu (B2B signál) má default ``net``; jinak
  // ``gross``. Persistuje se v localStorage. Toggle skryt pro
  // anonymous (CZ právo).
  const isLoggedIn = !!me.data?.email;

  // KAN-923 — address book → last order in this shop → profile; company,
  // IČO, DIČ and the phone's dial code never from the last order
  // (KAN-2093 — the BE decides, this only applies). Applied
  // once, and only into fields the customer has not typed into yet, so
  // a refetch never overwrites what is on the screen.
  const prefill = useCheckoutPrefill(
    { enabled: isLoggedIn || !!quoteCheckout },
    quoteCheckout,
  );
  const prefillData = prefill.data;
  const prefillApplied = useRef(false);
  useEffect(() => {
    if (prefillApplied.current) return;
    if (!prefillData || typeof prefillData !== "object") return;
    if (!("shipping_address" in prefillData)) return;
    prefillApplied.current = true;
    // KAN-1913 — země jde z téhož zdroje jako ulice a PSČ, pokud ji
    // zákazník sám nezměnil; výchozí země formuláře „vyplněná" není.
    const shipCountryFree = !countryTouched.current.shipping;
    setShipping((prev) =>
      fillEmptyFields(prev, prefillData.shipping_address, shipCountryFree),
    );
    if (prefillData.billing_address) {
      const billCountryFree = !countryTouched.current.billing;
      setBilling((prev) =>
        fillEmptyFields(prev, prefillData.billing_address, billCountryFree),
      );
      setBillingSame(false);
    }
    setCompany((prev) => ({
      ico: prev.ico || prefillData.ico || "",
      dic: prev.dic || prefillData.dic || "",
    }));
  }, [prefillData]);
  // KAN-2093 — the note names every source used (address / company /
  // phone can each come from a different one), never one for all.
  const prefillNote = checkoutPrefillNote(prefillData, tCo, lang);
  const looksLikeB2B = isLoggedIn && !!(me.data?.company || company.dic);
  // KAN-992 — pole „vaše číslo objednávky" vidí jen ten, kdo nakupuje
  // pod firemním účtem U TOHOHLE dodavatele. Členství v cizí firmě
  // (jiný tenant) se nepočítá: nákupní číselník je vždycky vztažený
  // k jednomu dodavateli. ``looksLikeB2B`` výš se na to použít nedá --
  // ten je jen o zobrazení cen s DPH / bez DPH a stačí mu vyplněné DIČ.
  const isCompanyBuyer = (me.data?.customer_org_memberships ?? []).some(
    (m) => m.provider_slug === branding.data?.slug,
  );
  // KAN-2100 — firma, která nakupuje bez firemního účtu (vyplnila IČO
  // v pokladně), má svůj nákupní číselník taky; pole bylo do té doby jen
  // v adminu detailu objednávky. Volitelné, propisuje se tam, kam to
  // admin pole (``Order.customer_reference``): potvrzení, dodací list,
  // faktura, ISDOC.
  const offersCustomerReference = isCompanyBuyer || company.ico.trim() !== "";
  // KAN-1432 — výchozí režim jde z toho, komu tenant prodává (průvodce).
  const checkoutBusinessInfo = useBusinessInfo().data;
  const segments = checkoutBusinessInfo?.buyer_segments ?? null;
  // KAN-1515 — neplátci se přepínač „S DPH | Bez DPH" nenabízí a řádky
  // souhrnu se nejmenují „… bez DPH": obojí slibuje daň, kterou neúčtuje.
  const { appliesVat } = storefrontVatDisplay(checkoutBusinessInfo);
  const [vatMode, setVatMode] = useState<VatDisplayMode>(() =>
    defaultVatDisplayMode(looksLikeB2B, segments),
  );
  useEffect(() => {
    setVatMode(defaultVatDisplayMode(looksLikeB2B, segments));
  }, [looksLikeB2B, segments]);
  const onVatModeChange = (mode: VatDisplayMode): void => {
    setVatMode(mode);
    writeVatDisplayPreference(mode);
  };

  // KAN-1456 — postal-code format and field order follow the country,
  // from the platform catalog. Mirrors the server rule
  // (``*_address_invalid_postal_code`` 422); a country without a format
  // is not checked.
  const { data: deliveryCountries } = useCheckoutDeliveryCountries();
  const addressHints = (addr: Address): AddressHints => {
    const country = deliveryCountries?.find(
      (c) => c.code === addr.country_code,
    );
    return {
      postalInvalid:
        addr.postal_code.trim() !== "" &&
        !postalCodeMatches(addr.postal_code, country?.postal_code_regex),
      postalFirst: postalCodeBeforeCity(country?.address_format),
      countryName: country
        ? pickI18nText(country.name_cs, country.name_en, lang)
        : addr.country_code,
      postalExample: country?.postal_code_example ?? null,
    };
  };
  const postalFormatMessage = (hints: AddressHints): string =>
    postalCodeFormatMessage(tCo, hints);
  const shippingHints = addressHints(shipping);
  const billingHints = addressHints(billing);

  // KAN-924 — mirrors the server rule (``invalid_company_id`` /
  // ``invalid_vat_id`` 422). Blank is fine, the fields are optional.
  const companyCountry = (billingSame ? shipping : billing).country_code;
  const icoInvalid = !isValidCompanyId(company.ico, companyCountry);
  // KAN-2013 — obchod jen pro firmy (průvodce: „Kdo u vás nakupuje?" =
  // firmy) prodává na fakturu s IČO: pole je povinné a spotřebitelské
  // poučení o odstoupení se v souhlasu nenabízí. Zrcadlí server
  // (``company_id_required`` 422).
  // KAN-2197 — ukázkový obchod nic nefakturuje, IČO by jen brzdilo návštěvníka.
  const businessOnly = segments === "b2b" && !branding.data?.is_platform_demo;
  const icoMissing = businessOnly && !company.ico.trim();
  // KAN-1522 — validace IČO zemi respektuje odjakživa (KAN-924),
  // vyhledávání ne: slovenské firmě se nabízelo „Vyhledat v ARES“,
  // což je ČESKÝ registr a pro SK nemůže uspět. Registr bereme
  // z téže tabulky jako pravidlo IČO — žádná podmínka na zemi.
  const companyRegistry = companyRegistryFor(companyCountry);
  const dicInvalid = !isValidVatId(company.dic);

  const aresLookup = useAresLookup({
    onSuccess: (rec) => {
      setCompany((p) => ({ ...p, dic: rec.dic ?? p.dic }));
      if (billingSame) setBillingSame(false);
      setBilling((prev) => ({
        ...prev,
        company: rec.name,
        street: rec.street ?? prev.street,
        city: rec.city ?? prev.city,
        postal_code: rec.postal_code ?? prev.postal_code,
        country_code: rec.country_code || prev.country_code,
      }));
    },
  });

  const selectedMultiplier = useMemo(() => {
    const opt = legacyDeliveryOptions.find((d) => d.code === deliveryCode);
    return opt?.multiplier ?? 1;
  }, [legacyDeliveryOptions, deliveryCode]);

  const baseMultiplier = useMemo(() => {
    const opt = legacyDeliveryOptions.find((d) => d.code === "standard");
    return opt?.multiplier ?? 1;
  }, [legacyDeliveryOptions]);

  const selectedLadderOption = useMemo(() => {
    if (!hasLadderRungs || ladderId === null) return null;
    return ladderOptions.data?.find((o) => o.ladder_id === ladderId) ?? null;
  }, [hasLadderRungs, ladderId, ladderOptions.data]);

  const ladderSurcharge = selectedLadderOption
    ? selectedLadderOption.flat_surcharge_czk +
      selectedLadderOption.multiplier_surcharge_czk
    : 0;

  // KAN-609 — this whole panel computes in MINOR units of the cart's books
  // currency and converts to major only where a number is formatted. It used
  // to start from the whole-unit ``subtotal_czk`` and round every derived
  // figure to a whole unit again, so for a tenant whose books carry cents the
  // last screen before payment disagreed with the cart one step earlier AND
  // with what checkout then stamped.
  const cartCurrency = cart.data?.currency_code;
  const subtotalMinor = minorFromPair(
    cart.data?.subtotal_minor,
    cart.data?.subtotal_czk,
    cartCurrency,
  );
  // Rate-table figures (ladder surcharge, carrier price) arrive as whole major
  // units; converting UP is exact, so this loses nothing.
  const ladderSurchargeMinor = majorToMinor(ladderSurcharge, cartCurrency);

  const previewSubtotalMinor = useMemo(() => {
    if (hasLadderRungs) {
      return subtotalMinor + ladderSurchargeMinor;
    }
    if (baseMultiplier === 0) return subtotalMinor;
    return Math.round((subtotalMinor * selectedMultiplier) / baseMultiplier);
  }, [
    subtotalMinor,
    hasLadderRungs,
    ladderSurchargeMinor,
    selectedMultiplier,
    baseMultiplier,
  ]);

  const deliverySurchargeMinor = previewSubtotalMinor - subtotalMinor;

  const showDeliveryAdjustment =
    step === "delivery" && deliverySurchargeMinor !== 0;

  const carrierPrice = useMemo<number | null>(() => {
    if (isPickup) {
      return pickupOffer
        ? minorToMajor(pickupOffer.price_minor, pickupOffer.currency_code)
        : null;
    }
    const list = Array.isArray(carriers.data) ? carriers.data : [];
    const row = list.find((c) => c.code === carrier);
    return row?.price_czk ?? null;
  }, [carriers.data, carrier, isPickup, pickupOffer]);
  // "Počet dní navíc pro dopravu" of the selected carrier (carrier +
  // country + matched weight band, summed by the BE). Added on top of
  // the production days when rendering the promised delivery date.
  // Osobní odběr: termín = dokončení výroby, žádné dny na přepravu.
  const carrierExtraDays = useMemo<number>(() => {
    if (isPickup) return pickupOffer?.extra_days ?? 0;
    const list = Array.isArray(carriers.data) ? carriers.data : [];
    const row = list.find((c) => c.code === carrier);
    return row?.extra_days ?? 0;
  }, [carriers.data, carrier, isPickup, pickupOffer]);
  // KAN-1623 — datum DORUČENÍ počítá server: tytéž úrovně, ale s dny
  // přepravy zvoleného dopravce (v pracovních dnech, jako ETA objednávky).
  // Do teď je sem přičítal prohlížeč kalendářně a páteční předání +1 den
  // slibovalo sobotu. Při nulovém tranzitu je to tentýž dotaz (cache).
  // KAN-1643 — a přeprava přeskočí státní svátky země doručení.
  const ladderDates = useCartDeliveryLadderOptions(
    undefined,
    carrierExtraDays,
    shipping.country_code || undefined,
  );
  const deliveredOn = (opt: CartDeliveryLadderOption): Date | null => {
    const dated = ladderDates.data?.find((o) => o.ladder_id === opt.ladder_id);
    return dated ? deliveryDateFor(dated) : null;
  };
  const noCarriersConfigured =
    carriers.isSuccess &&
    carriers.data.length === 0 &&
    !pickupOffer &&
    !shippingFitsNowhere;
  const singleLadderOption =
    hasLadderRungs && ladderOptions.data!.length === 1
      ? ladderOptions.data![0]
      : null;
  // KAN-1756 (KAN-484 varianta B) — obchod bez kapacitních stupňů termíny
  // nenabízí: zákazník vidí jednu rychlost bez volby a pokladna se tím
  // NEBLOKUJE. ``delivery_ready`` říká přesně „má aktivní stupně".
  const deliveryDatesOff =
    branding.isSuccess && branding.data?.shop_readiness?.delivery_ready === false;
  // Stupně se nabízejí podle obsahu košíku; prázdný košík je nemá z čeho
  // spočítat, takže „obchod nemá termíny dodání" bylo u prázdné pokladny
  // nepravdivé (KAN-1796).
  const cartHasLines =
    !!cart.data && cart.data.items.reduce((n, i) => n + i.lines.length, 0) > 0;
  const noDeliveryTiersConfigured =
    cartHasLines &&
    ladderOptions.isSuccess &&
    catalog.isSuccess &&
    !deliveryDatesOff &&
    !hasLadderRungs &&
    !hasLegacyDeliveryTiers;
  const checkoutCurrency = branding.data?.checkout_currency ?? "CZK";
  const checkoutFxRate = branding.data?.checkout_fx_rate_to_czk ?? 1;
  const shopReadiness = branding.data?.shop_readiness;
  const shopBlocksCheckout =
    branding.isSuccess && shopReadiness?.checkout_ready === false;
  // KAN-764 - the submit button used to promise a card payment to every
  // customer of every shop, including the many that have no Stripe
  // account at all: for them the promise was false and the order was
  // really going to be settled by transfer. Only a POSITIVE "no cards"
  // switches the copy; while branding is in flight the answer is
  // unknown, and the card wording is the safer of the two guesses
  // (a shop that does take cards must not tell people to transfer).
  const transferOnly =
    branding.isSuccess && shopReadiness?.card_payments_ready === false;

  // KAN-993 — čím zákazník zaplatí. Nabídku určuje server, protože
  // závisí na tom, KDO se ptá: „převodem na fakturu" se podle nastavení
  // tenanta nabízí jen členovi firemního účtu, nebo jen přihlášenému.
  const paymentMethodsQuery = useCheckoutPaymentMethods();
  const offeredPaymentMethods = paymentMethodsQuery.data?.methods ?? [];
  // ``null`` = zákazník ještě nevybral. Výchozí se ODVOZUJE z nabídky
  // místo ukládání efektem: nabídka dorazí až po prvním renderu a
  // ``useEffect``, který by ji dosadil do stavu, by mezitím nechal
  // pokladnu ve stavu „nevybráno" a při změně přihlášení by dosazoval
  // podruhé přes zákazníkovu volbu.
  const [paymentMethod, setPaymentMethod] = useState<
    "card" | "bank_transfer" | "account" | null
  >(null);
  const effectivePaymentMethod =
    paymentMethod && offeredPaymentMethods.includes(paymentMethod)
      ? paymentMethod
      : (offeredPaymentMethods[0] ?? null);
  // Kopie tlačítka a řádku pod ním se řídí VYBRANÝM způsobem. Dokud
  // nabídka nedorazila, platí starý globální odhad — ze stejného
  // důvodu jako výš: říct zákazníkovi „pošlete převodem" v obchodě,
  // který bere karty, je ta horší ze dvou špatných domněnek.
  const paysByTransfer =
    effectivePaymentMethod !== null
      ? effectivePaymentMethod !== "card"
      : transferOnly;

  // The subtotal-limit errors arrive as raw CZK params. Everything else on
  // this page renders through ``formatMoney``, so leaving them unformatted
  // put a crown figure next to converted totals on a non-CZK checkout.
  const money = (value: unknown): string =>
    formatMoney(Number(value), checkoutCurrency, locale, checkoutFxRate);

  // KAN-609 — the summary panel computes in minor units, so it formats through
  // this one. Deliberately a SECOND function rather than a wider ``money``:
  // one taking major and one taking minor look identical at the call site, and
  // a mix-up is a 100x error on the last screen before payment.
  const moneyMinor = (minor: number): string =>
    money(minorToMajor(minor, cartCurrency));

  // KAN-739 / KAN-1692: říká zákazníkovi PŘED submitem, proč objednávka
  // neprojde, místo aby na 422 narazil až tlačítkem. Verdikt NEPOČÍTÁME —
  // čteme ho ze serveru (``GET /cart`` → ``below_min_subtotal``, tatáž
  // funkce jako vynucení v pokladně, včetně dorovnání minima). Vlastní
  // porovnání s ``landing_facts.min_subtotal_czk`` zamykalo „Zaplatit"
  // i obchodu, jehož dorovnání hranici pokrývá.
  const belowMinSubtotal = cart.data?.below_min_subtotal === true;
  const minSubtotalMinor = cart.data?.min_subtotal_minor ?? null;
  const cartSubtotalMinor = cart.data?.subtotal_minor ?? null;

  // KAN-1883 — odeslání by vytvořilo objednávku na jinou částku, než jakou
  // ukázal souhrn (obchod mezitím změnil ceník). Server nic nezapsal a vrátil
  // obě částky; zákazník novou vidí a dalším kliknutím ji potvrzuje
  // (``confirmed_total_minor``). Bez potvrzení objednávka nevznikne.
  const priceChange =
    checkout.error instanceof ApiError &&
    checkout.error.code === "price_changed"
      ? {
          previousMinor: Number(checkout.error.params.previous_total_minor),
          currentMinor: Number(checkout.error.params.current_total_minor),
        }
      : null;

  const errorMessage = (() => {
    if (demoEnd.error) return tCo("demoRedirectFailed");
    if (!checkout.error) return null;
    if (checkout.error instanceof ApiError) {
      switch (checkout.error.code) {
        case "cart_empty":
          return tCo("errors.cartEmpty");
        case "shipping_unavailable":
          return tCo("errors.shippingUnavailable", {
            country: checkout.error.params.country_code,
          });
        case "unknown_carrier":
          return tCo("errors.unknownCarrier");
        case "pickup_not_available":
          return tCo("errors.pickupNotAvailable");
        case "shipping_address_invalid_postal_code":
        case "billing_address_invalid_postal_code":
          return postalFormatMessage(
            addressHints({
              ...shipping,
              country_code: String(checkout.error.params.country_code ?? ""),
            }),
          );
        case "invalid_company_id":
          return tCo("errors.invalidCompanyId");
        case "company_id_required":
          return tCo("errors.companyIdRequired");
        case "invalid_vat_id":
          return tCo("errors.invalidVatId");
        case "terms_not_accepted":
          return tCo(
            "errors.termsNotAccepted",
            "Bez souhlasu s obchodními podmínkami nelze objednávku odeslat.",
          );
        case "order_items_exceed_max":
          return tCo("errors.orderItemsExceedMax", {
            items_count: formatCount(checkout.error.params.items_count, locale),
            max_items: formatCount(checkout.error.params.max_items, locale),
          });
        case "line_qty_exceeds_max":
          return tCo("errors.lineQtyExceedsMax", {
            quantity: formatCount(checkout.error.params.quantity, locale),
            max_quantity: formatCount(checkout.error.params.max_quantity, locale),
          });
        case "order_below_min_subtotal":
          return tCo("errors.orderBelowMinSubtotal", {
            subtotal: money(checkout.error.params.subtotal_czk),
            min_subtotal: money(checkout.error.params.min_subtotal_czk),
          });
        case "order_above_max_subtotal":
          return tCo("errors.orderAboveMaxSubtotal", {
            subtotal: money(checkout.error.params.subtotal),
            max_subtotal: money(checkout.error.params.max_subtotal),
          });
        case "order_exceeds_max_weight":
          return tCo("errors.orderExceedsMaxWeight", {
            weight_kg: formatKgAmount(checkout.error.params.weight_kg, locale),
            max_weight_kg: formatKgAmount(checkout.error.params.max_weight_kg, locale),
          });
        case "shipping_does_not_fit":
          return tCo("errors.shippingDoesNotFit");
        case "no_machine_fits":
          return tCo("errors.noMachineFits", {
            files: (checkout.error.params.file_names as string[] | undefined)?.join(", ") ?? "",
          });
        case "price_changed":
          return tCo("errors.priceChanged", {
            previous: moneyMinor(priceChange?.previousMinor ?? 0),
            current: moneyMinor(priceChange?.currentMinor ?? 0),
          });
        default:
          return tCo("errors.generic");
      }
    }
    return tCo("errors.generic");
  })();

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step !== "delivery") return;
    if (branding.data?.is_platform_demo) {
      demoEnd.mutate();
      return;
    }
    checkout.mutate({
      shipping_address: shipping,
      billing_address: billingSame ? undefined : billing,
      ...(isPickup
        ? { delivery_method: "pickup" as const }
        : { carrier_code: carrier }),
      ico: company.ico || undefined,
      dic: company.dic || undefined,
      ...(offersCustomerReference && customerReference.trim()
        ? { customer_reference: customerReference.trim() }
        : {}),
      currency: checkoutCurrency,
      // Ladder picker is the canonical payload (position picker dropped
      // in PR14). Tenants without ladder rungs fall back to the legacy
      // ``delivery_code`` dropdown which BE handles via its own
      // ``catalog.delivery_options`` path.
      ...(hasLadderRungs && ladderId !== null
        ? { delivery_ladder_id: ladderId }
        : {}),
      terms_accepted: termsAccepted,
      // KAN-993 — posílá se jen tehdy, když zákazník OPRAVDU vybíral.
      // U obchodu s jedinou možností by zápis tvrdil rozhodnutí, které
      // se nekonalo, a objednávky obchodu bez Stripe by nesly
      // ``bank_transfer`` napevno: kdyby si Stripe zapnul, ty
      // rozpracované by kartou zaplatit nešly. Bez volby zůstane pole
      // prázdné a čtenáři odpovídají po staru.
      ...(offeredPaymentMethods.length > 1 && effectivePaymentMethod
        ? { payment_method: effectivePaymentMethod }
        : {}),
      ...(isTestOrder ? { is_test: true } : {}),
      // Jazyk, ve kterém zákazník podmínky opravdu viděl — doklad musí
      // nést tuhle verzi, ne serverový default.
      terms_lang: lang,
      ...(priceChange
        ? { confirmed_total_minor: priceChange.currentMinor }
        : {}),
    });
  };

  const cartIsEmpty =
    cart.data && cart.data.items.reduce((n, i) => n + i.lines.length, 0) === 0;
  // Nabídka, ze které už objednat nejde (prošlá, zrušená, už objednaná,
  // cizí) — backend to řekne kódem na čtení košíku.
  const quoteCheckoutError =
    quoteCheckout && cart.error instanceof ApiError ? cart.error.code : null;

  return (
    <>
      <main className="mx-auto max-w-5xl px-6 pb-16">
        <div className="mt-6 flex items-end justify-between">
          <h1 className="text-3xl font-semibold tracking-tight text-ink">
            {tCo("title")}
          </h1>
          {!cartIsEmpty && (
            <Pill tone="neutral" data-testid="step-indicator">
              {tCo("step.indicator", {
                current: step === "address" ? 1 : 2,
                total: 2,
              })}{" "}
              · {tCo(step === "address" ? "step.address" : "step.delivery")}
            </Pill>
          )}
        </div>

        <div className="mt-4 space-y-3">
          {quoteCheckout && (
            <Card
              padded
              elevation="flat"
              className="border-border-strong"
              data-testid="checkout-quote-note"
            >
              <p className="text-sm text-ink-2">{tCo("quote.note")}</p>
            </Card>
          )}
          {/* KAN-2013 — věta „Tento obchod momentálně účtuje v měně CZK."
              je pryč (rozhodnutí vlastníka): měna stojí u každé částky
              a zákazník jednoměnového obchodu nevěděl, co mu chce říct. */}
          {(noCarriersConfigured ||
            noDeliveryTiersConfigured ||
            shopBlocksCheckout) && (
            <Card
              padded
              elevation="flat"
              className="border-warn-border bg-warn-bg"
            >
              <p
                className="text-sm text-warn"
                data-testid="checkout-readiness-warning"
              >
                {shopBlocksCheckout
                  ? tCo("readiness.shopNotReady")
                  : noDeliveryTiersConfigured
                    ? tCo("readiness.noDeliveryTiers", {
                        defaultValue:
                          lang === "cs"
                            ? "Tento obchod zatím nemá nastavené termíny dodání, takže online checkout je momentálně blokovaný."
                            : "This shop does not have delivery tiers configured yet, so online checkout is currently blocked.",
                      })
                    : tCo("readiness.noCarriers")}
              </p>
            </Card>
          )}
        </div>

        {quoteCheckoutError ? (
          <Card
            padded
            elevation="raised"
            className="mt-8 border-warn-border bg-warn-bg"
            data-testid="checkout-quote-error"
          >
            <p className="text-sm text-warn">
              {tCo(`quote.errors.${quoteCheckoutError}`, {
                defaultValue: tCo("quote.errors.generic"),
              })}
            </p>
            <Link
              to={`/${lang}/quotes/by-id/${encodeURIComponent(quoteCheckout ?? "")}`}
              className="mt-3 inline-block text-sm underline"
            >
              {tCo("quote.backToQuote")}
            </Link>
          </Card>
        ) : cartIsEmpty ? (
          <Card
            padded
            elevation="raised"
            className="mt-8 border-dashed border-border-strong text-center"
          >
            <p className="text-ink-2">{tCart("empty")}</p>
            <EmptyCartUpload lang={lang} />
          </Card>
        ) : (
          <form onSubmit={onSubmit} className="mt-8 grid gap-8 md:grid-cols-3">
            <div className="space-y-8 md:col-span-2">
              {step === "address" && (
                <>
                  {prefillNote && (
                    <p
                      className="text-sm text-ink-3"
                      data-testid="checkout-prefill-note"
                    >
                      {prefillNote}
                    </p>
                  )}
                  <AddressFields
                    value={shipping}
                    onChange={onShippingChange}
                    heading={tCo("shippingHeading")}
                    testPrefix="ship"
                    hints={shippingHints}
                  />

                  <label className="flex items-center gap-2 text-sm text-ink-2">
                    <input
                      type="checkbox"
                      checked={billingSame}
                      onChange={(e) => setBillingSame(e.target.checked)}
                      data-testid="checkout-billing-same"
                    />
                    {tCo("billingSameAsShipping")}
                  </label>

                  {!billingSame && (
                    <AddressFields
                      value={billing}
                      onChange={onBillingChange}
                      heading={tCo("billingHeading")}
                      testPrefix="bill"
                      hints={billingHints}
                    />
                  )}

                  <Card padded elevation="flat">
                    <h2 className="mb-3 text-lg font-semibold tracking-snug text-ink">
                      {tCo(
                        businessOnly
                          ? "companyHeadingRequired"
                          : "companyHeading",
                      )}
                    </h2>
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="flex items-end gap-2">
                        <div className="flex-1">
                          <Field
                            label={tCo("field.ico")}
                            required={businessOnly}
                            value={company.ico}
                            onChange={(ico) =>
                              setCompany((p) => ({ ...p, ico }))
                            }
                            name="ico"
                            invalid={icoInvalid}
                          />
                          {icoInvalid && (
                            <p
                              className="mt-1 text-sm text-danger"
                              role="alert"
                              data-testid="ico-invalid"
                            >
                              {tCo("validation.invalidIco")}
                            </p>
                          )}
                        </div>
                        {companyRegistry && (
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            loading={aresLookup.isPending}
                            onClick={() => {
                              if (
                                !guardAres.check(
                                  !company.ico.trim() &&
                                    tCo("validation.icoRequired", {
                                      defaultValue:
                                        "Vyplňte IČO, které máme vyhledat.",
                                    }),
                                )
                              ) {
                                return;
                              }
                              aresLookup.mutate(company.ico.trim());
                            }}
                            className="h-[38px]"
                            data-testid="ares-lookup"
                          >
                            {aresLookup.isPending
                              ? tCo("aresLookupLoading")
                              : tCo("registryLookup", {
                                  registry: companyRegistry,
                                })}
                          </Button>
                        )}
                      </div>
                      <SubmitBlockers blockers={guardAres.blockers} />
                      <div>
                        <Field
                          label={tCo("field.dic")}
                          value={company.dic}
                          onChange={(dic) =>
                            setCompany((p) => ({ ...p, dic }))
                          }
                          name="dic"
                          invalid={dicInvalid}
                        />
                        {dicInvalid && (
                          <p
                            className="mt-1 text-sm text-danger"
                            role="alert"
                            data-testid="dic-invalid"
                          >
                            {tCo("validation.invalidDic")}
                          </p>
                        )}
                      </div>
                    </div>
                    {offersCustomerReference && (
                      <div className="mt-3">
                        <Field
                          label={tCo("field.customerReference")}
                          value={customerReference}
                          onChange={setCustomerReference}
                          name="customer_reference"
                        />
                        <p className="mt-1 text-sm text-ink-3">
                          {tCo("customerReferenceHint")}
                        </p>
                      </div>
                    )}
                    {aresLookup.error && (
                      <p
                        className="mt-2 text-sm text-danger"
                        role="alert"
                        data-testid="checkout-ares-error"
                      >
                        {aresLookup.error instanceof ApiError &&
                        aresLookup.error.code === "ares_not_found"
                          ? tCo("aresNotFound")
                          : aresLookup.error instanceof ApiError &&
                              aresLookup.error.code === "invalid_ico"
                            ? tCo("aresInvalidIco")
                            : tCo("aresGenericError")}
                      </p>
                    )}
                  </Card>
                </>
              )}

              {step === "delivery" && (
                <>
                  <Card padded elevation="flat">
                    <h2 className="mb-3 text-lg font-semibold tracking-snug text-ink">
                      {tCo("deliveryHeading")}
                    </h2>
                    {!deliveryDatesOff && (
                      <p className="mb-3 text-sm text-ink-3">
                        {tCo("deliveryLead")}
                      </p>
                    )}
                    {deliveryDatesOff ? (
                      // KAN-1756 — bez kapacitních stupňů jedna rychlost,
                      // žádná volba termínu ani slíbené datum.
                      <div
                        className="rounded-md border border-border-strong bg-surface px-3 py-2 text-sm text-ink-2"
                        data-testid="delivery-single-speed"
                      >
                        <span className="font-medium text-ink">
                          {tCo("deliverySingleSpeed")}
                        </span>
                        <p className="mt-1 text-xs text-ink-3">
                          {tCo("deliverySingleSpeedNote")}
                        </p>
                      </div>
                    ) : singleLadderOption ? (
                      // KAN-925 — one rung left after the BE hid the
                      // ones that did not differ from it: nothing to
                      // choose, so say so instead of showing one radio.
                      <div
                        className="rounded-md border border-border-strong bg-surface px-3 py-2 text-sm text-ink-2"
                        data-testid="ladder-delivery-single"
                      >
                        <span className="font-medium text-ink">
                          {pickI18nBlob(
                            singleLadderOption.ladder_label_i18n,
                            singleLadderOption.ladder_label_cz,
                            singleLadderOption.ladder_label_en,
                            lang,
                          )}
                        </span>
                        {/* KAN-1922 — bez slíbeného termínu ani počet dní:
                            nezná ho operace bez termínů / bez odhadu doby. */}
                        {!singleLadderOption.eta_pending_estimate && (
                          <span className="mono ml-2 text-xs text-ink-3">
                            {tCo(...ladderDaysLabel(singleLadderOption))}
                          </span>
                        )}
                        <p className="mt-1 text-[11px] text-ink-3">
                          {/* KAN-1605 — u osobního odběru je termín
                              „připraveno k vyzvednutí“, ne doručení. */}
                          {tCo(isPickup ? "pickupReadyDate" : "deliveryDate", {
                            // KAN-1438 — krok bez odhadu doby: po platbě
                            // „Termín upřesníme“, tak ho neslibujme ani tady.
                            date: singleLadderOption.eta_pending_estimate
                              ? tCo(pendingDateKey(singleLadderOption))
                              : formatDeliveryDate(
                                  deliveredOn(singleLadderOption),
                                  lang === "cs" ? "cs-CZ" : "en-US",
                                ),
                          })}
                        </p>
                        <p className="mt-1 text-xs text-ink-3">
                          {tCo("deliverySingleOption")}
                        </p>
                      </div>
                    ) : hasLadderRungs ? (
                      <div
                        className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3"
                        role="radiogroup"
                        aria-label={tCo("deliveryHeading")}
                        data-testid="ladder-delivery-options"
                      >
                        {[...ladderOptions.data!]
                          .sort(
                            (a, b) => a.ladder_sort_order - b.ladder_sort_order,
                          )
                          .map((opt, idx, sorted) => {
                            const selected = opt.ladder_id === ladderId;
                            // Slowest active rung sets the reference
                            // (same convention as the position picker).
                            const isReference = idx === 0;
                            // Compare against the slowest rung's flat
                            // surcharge so "same price" reads cleanly
                            // when admin set non-zero flat on every
                            // rung but kept the deltas at zero.
                            const baseline = sorted[0];
                            const surchargeDelta =
                              opt.flat_surcharge_czk +
                              opt.multiplier_surcharge_czk -
                              (baseline.flat_surcharge_czk +
                                baseline.multiplier_surcharge_czk);
                            const surchargeLabel = isReference
                              ? tCo("deliveryNoSurcharge")
                              : surchargeDelta > 0
                                ? `+${money(surchargeDelta)}`
                                : surchargeDelta < 0
                                  ? money(surchargeDelta)
                                  : tCo("deliverySamePrice");
                            return (
                              <button
                                key={opt.ladder_id}
                                type="button"
                                role="radio"
                                aria-checked={selected}
                                onClick={() => setLadderId(opt.ladder_id)}
                                className={cx(
                                  "flex flex-col items-start rounded-md border px-3 py-2 text-left text-sm transition-colors",
                                  selected
                                    ? "border-ink bg-ink text-bg"
                                    : "border-border-strong bg-surface text-ink-2 hover:bg-surface-2",
                                )}
                                data-testid={`ladder-delivery-option-${opt.ladder_code}`}
                              >
                                <span className="font-medium">
                                  {pickI18nBlob(
                                    opt.ladder_label_i18n,
                                    opt.ladder_label_cz,
                                    opt.ladder_label_en,
                                    lang,
                                  )}
                                </span>
                                <span
                                  className={cx(
                                    "mono text-xs",
                                    selected ? "text-white/80" : "text-ink-3",
                                  )}
                                >
                                  {tCo(
                                    opt.day_basis === "business" &&
                                      opt.days_business != null
                                      ? "deliveryBusinessDays"
                                      : opt.day_basis === "mixed"
                                        ? "deliveryMixedDays"
                                        : // KAN-1623 — kalendářní úroveň je
                                          // kalendářní; „deliveryDays" říká
                                          // „pracovní den".
                                          "deliveryCalendarDays",
                                    {
                                      count:
                                        opt.day_basis === "business" &&
                                        opt.days_business != null
                                          ? opt.days_business
                                          : opt.days_calendar,
                                    },
                                  )}
                                  {` · ${surchargeLabel}`}
                                </span>
                                <span
                                  className={cx(
                                    "mt-1 text-[11px]",
                                    selected ? "text-white/70" : "text-ink-3",
                                  )}
                                >
                                  {tCo(
                                    isPickup
                                      ? "pickupReadyDate"
                                      : "deliveryDate",
                                    {
                                      date: opt.eta_pending_estimate
                                        ? tCo(pendingDateKey(opt))
                                        : formatDeliveryDate(
                                            // The BE's delivery date: the
                                            // order-ETA function incl. the
                                            // selected carrier's transit
                                            // (KAN-1623).
                                            deliveredOn(opt),
                                            lang === "cs" ? "cs-CZ" : "en-US",
                                          ),
                                    },
                                  )}
                                </span>
                                {tiersDifferPerOperation(opt) && (
                                  <span
                                    className={cx(
                                      "mt-1 text-[11px]",
                                      selected ? "text-white/70" : "text-ink-3",
                                    )}
                                  >
                                    {opt.per_capacity
                                      .map(
                                        (c) =>
                                          `${formatProcessType(c.process_type)}: ${pickI18nBlob(
                                            c.label_i18n,
                                            c.label_cz,
                                            c.label_en,
                                            lang,
                                          )}`,
                                      )
                                      .join(" · ")}
                                  </span>
                                )}
                                {rungsDifferPerOperation(opt) && (
                                  <span
                                    className={cx(
                                      "mt-1 rounded px-1 text-[11px]",
                                      selected
                                        ? "bg-white/15 text-white"
                                        : "bg-warn-bg text-warn",
                                    )}
                                    data-testid={`ladder-fallback-${opt.ladder_code}`}
                                  >
                                    {/* KAN-1921 — název úrovně, ne její kód. */}
                                    {opt.fallbacks
                                      .flatMap((f) => {
                                        const label = fallbackRungLabel(
                                          opt,
                                          f,
                                          sorted,
                                          lang,
                                        );
                                        return label
                                          ? [
                                              `${formatProcessType(f.process_type)} → ${label}`,
                                            ]
                                          : [];
                                      })
                                      .join(" · ")}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                      </div>
                    ) : (
                      <div
                        className="grid gap-2 sm:grid-cols-3"
                        role="radiogroup"
                        aria-label={tCo("deliveryHeading")}
                        data-testid="delivery-options"
                      >
                        {(catalog.data?.delivery_options ?? []).map((d) => {
                          const selected = d.code === deliveryCode;
                          const pct = Math.round((d.multiplier - 1) * 100);
                          // "standard" is the baseline tier — show "no
                          // surcharge" rather than a confusingly empty
                          // label. Tiers without a real price diff (some
                          // tenants only differentiate by lead time) get
                          // an explicit "same price" so the customer
                          // doesn't think the label just failed to render.
                          const isReference = d.code === "standard";
                          let surchargeLabel: string;
                          if (isReference) {
                            surchargeLabel = tCo("deliveryNoSurcharge");
                          } else {
                            const parts: string[] = [];
                            if (pct > 0) parts.push(`+${pct}%`);
                            else if (pct < 0) parts.push(`${pct}%`);
                            if (d.flat_surcharge_czk > 0)
                              parts.push(`+${money(d.flat_surcharge_czk)}`);
                            surchargeLabel =
                              parts.length > 0
                                ? parts.join(" · ")
                                : tCo("deliverySamePrice");
                          }
                          const totalQty =
                            cart.data?.items.reduce(
                              (n, item) =>
                                n +
                                item.lines.reduce((q, l) => q + l.quantity, 0),
                              0,
                            ) ?? 0;
                          const effectiveDays = computeEffectiveDays(
                            d,
                            totalQty,
                          );
                          return (
                            <button
                              key={d.code}
                              type="button"
                              role="radio"
                              aria-checked={selected}
                              onClick={() => setDeliveryCode(d.code)}
                              className={cx(
                                "flex flex-col items-start rounded-md border px-3 py-2 text-left text-sm transition-colors",
                                selected
                                  ? "border-ink bg-ink text-bg"
                                  : "border-border-strong bg-surface text-ink-2 hover:bg-surface-2",
                              )}
                              data-testid={`delivery-option-${d.code}`}
                            >
                              <span className="font-medium">
                                {tCo(`deliveryTiers.${d.code}`, {
                                  defaultValue: d.code,
                                })}
                              </span>
                              <span
                                className={cx(
                                  "mono text-xs",
                                  selected ? "text-white/80" : "text-ink-3",
                                )}
                              >
                                {tCo("deliveryDays", { count: effectiveDays })}
                                {` · ${surchargeLabel}`}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </Card>

                  <Card padded elevation="flat">
                    <h2 className="mb-3 text-lg font-semibold tracking-snug text-ink">
                      {tCo("shippingMethodHeading")}
                    </h2>
                    <select
                      value={carrier}
                      onChange={(e) => setCarrier(e.target.value)}
                      disabled={!carriers.data?.length && !pickupOffer}
                      aria-label={tCo("shippingMethodHeading")}
                      className="block h-10 w-full rounded-md border border-border-strong bg-surface px-3 text-sm text-ink hover:border-ink-3 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                      data-testid="carrier"
                    >
                      {carriers.data?.map((c) => {
                        // Název v jazyce zákazníka (blob per jazyk platformy, KAN-1804);
                        // bez překladu platí výchozí jazyk.
                        const cName = pickI18nBlob(c.name_i18n, c.name, c.name_en, lang);
                        const parts = [cName];
                        if (c.price_czk != null) {
                          parts.push(
                            formatMoney(
                              c.price_czk,
                              checkoutCurrency,
                              locale,
                              checkoutFxRate,
                            ),
                          );
                        }
                        if (c.extra_days != null && c.extra_days > 0) {
                          parts.push(
                            tCo("carrierExtraDays", { count: c.extra_days }),
                          );
                        }
                        return (
                          <option key={c.code} value={c.code}>
                            {parts.join(" — ")}
                          </option>
                        );
                      })}
                      {shippingFitsNowhere && !carriers.data?.length && (
                        <option value="" disabled>
                          {tCo("shippingOnRequest")}
                        </option>
                      )}
                      {pickupOffer && (
                        <option value={PICKUP_OPTION}>
                          {[
                            tCo("pickupOption"),
                            formatMoney(
                              minorToMajor(
                                pickupOffer.price_minor,
                                pickupOffer.currency_code,
                              ),
                              checkoutCurrency,
                              locale,
                              checkoutFxRate,
                            ),
                            pickupOffer.location.address,
                          ]
                            .filter(Boolean)
                            .join(" — ")}
                        </option>
                      )}
                    </select>
                    {isPickup && pickupOffer && (
                      <PickupPlace pickup={pickupOffer} lang={lang} />
                    )}
                    {/* KAN-1903 — the gate is about CARRIERS; a customer
                        who picked up in person has nothing to enquire. */}
                    {shippingFitsNowhere && !isPickup && (
                      <div className="mt-2">
                        <ShippingDoesNotFitGate
                          lang={lang}
                          defaultEmail={me.data?.email}
                          pickupAvailable={Boolean(pickupOffer)}
                        />
                      </div>
                    )}
                    {noCarriersConfigured && (
                      <p
                        className="mt-2 text-sm text-ink-3"
                        data-testid="no-carriers"
                      >
                        {tCo("noCarriersAvailable", {
                          defaultValue:
                            lang === "cs"
                              ? "Pro tento účet není nakonfigurován žádný dopravce."
                              : "No shipping methods configured.",
                        })}
                      </p>
                    )}
                  </Card>

                  <button
                    type="button"
                    onClick={() => setStep("address")}
                    className="text-sm text-ink-3 hover:text-ink"
                    data-testid="back-to-address"
                  >
                    {tCo("step.back")}
                  </button>
                </>
              )}
            </div>

            <Card
              padded
              elevation="raised"
              className="h-fit space-y-3 md:sticky md:top-6"
            >
              <div className="flex items-start justify-between gap-3">
                <h2 className="mono text-[11px] uppercase tracking-[0.08em] text-ink-3">
                  {tCo("summaryHeading")}
                </h2>
                <VatDisplayToggle
                  mode={vatMode}
                  onChange={onVatModeChange}
                  visible={isLoggedIn && appliesVat}
                />
              </div>
              <div
                className="flex justify-between text-sm"
                data-testid="summary-subtotal-net"
              >
                <span className="text-ink-3">
                  {appliesVat
                    ? tCo("summarySubtotalNet", {
                        defaultValue: "Mezisoučet bez DPH",
                      })
                    : tCo("summarySubtotal", { defaultValue: "Mezisoučet" })}
                </span>
                <span className="mono tabular-nums text-ink">
                  {/* KAN-2093 — bez košíku i bez souhrnu ze serveru není
                      co ukázat: „0 Kč" tu dřív stálo ~10 s po přidání
                      do košíku a vypadalo jako skutečná částka. */}
                  {totalsPreview.data || cart.data
                    ? moneyMinor(
                        totalsPreview.data?.subtotal_minor ?? subtotalMinor,
                      )
                    : tCo("summaryLoading")}
                </span>
              </div>
              {carrierPrice != null && (
                <div
                  className="flex justify-between text-sm"
                  data-testid="summary-shipping"
                >
                  <span className="text-ink-3">
                    {isPickup
                      ? tCo("pickupOption")
                      : appliesVat
                        ? tCo("summaryShippingNet", {
                            defaultValue: "Doprava bez DPH",
                          })
                        : tCo("summaryShipping", { defaultValue: "Doprava" })}
                  </span>
                  <span className="mono tabular-nums text-ink">
                    {formatMoney(
                      carrierPrice,
                      checkoutCurrency,
                      locale,
                      checkoutFxRate,
                    )}
                  </span>
                </div>
              )}
              {showDeliveryAdjustment && (
                <div
                  className="flex justify-between text-sm"
                  data-testid="summary-delivery"
                >
                  <span className="text-ink-3">
                    {tCo("summaryDeliveryLine")}
                  </span>
                  <span className="mono tabular-nums text-ink">
                    {moneyMinor(
                      totalsPreview.data?.delivery_surcharge_minor ??
                        deliverySurchargeMinor,
                    )}
                  </span>
                </div>
              )}
              {(() => {
                // KAN-1514 — DPH, dorovnání a celek počítá server týmž
                // enginem, jaký objednávku zapíše. Dokud odpověď
                // nedorazí, nekreslí se nic: druhý výpočet tady byl
                // přesně ta vada, kvůli které pokladna ukazovala 619
                // tam, kde objednávka i doklad říkaly 500.
                const preview = totalsPreview.data;
                if (!preview) return null;
                const vatRate = Number(preview.vat_rate_pct);
                // KAN-2013 — tytéž řádky jako souhrn košíku.
                const totals = orderSummaryTotals({
                  totalMinor: preview.total_minor,
                  vatMinor: preview.vat_minor,
                  vatMode,
                });
                const totalVatMinor = totals.vatMinor;
                const topupMinor = preview.minimum_order_topup_minor;
                const minimumOrderMinor = preview.minimum_order_value_minor;
                const grandTotalMinor = totals.grossMinor;
                // KAN-1514 — dorovnání se zákazníkovi ukazovalo jako
                // poplatek bez vysvětlení: nikde nestálo, jaké minimum
                // platí ani kolik do něj chybí. Popisek doplňuje obojí;
                // bez známého minima (starší odpověď) se nevykreslí.
                // KAN-2055 — dorovnání minim skupin operací (řádek za
                // pravidlo obchodu); patří do součtu rozpadu jako dorovnání.
                const groupTopupRows = (preview.operation_group_topups ?? [])
                  .filter((row) => row.amount_minor > 0)
                  .map((row) => (
                    <div
                      key={row.name}
                      className="flex justify-between text-sm"
                      data-testid="summary-operation-group-topup"
                    >
                      <span className="text-ink-3">
                        {/* KAN-2078 — příplatek za zakázku nese vlastní jméno. */}
                        {row.kind === "order_surcharge"
                          ? row.name
                          : tCo("summaryOperationGroupTopup", {
                              name: row.name,
                              defaultValue: "Dorovnání minima — {{name}}",
                            })}
                      </span>
                      <span className="mono tabular-nums text-ink">
                        {moneyMinor(row.amount_minor)}
                      </span>
                    </div>
                  ));
                const topupRow =
                  topupMinor > 0 || groupTopupRows.length > 0 ? (
                    <>
                      {groupTopupRows}
                      {topupMinor > 0 && (
                      <>
                      <div
                        className="flex justify-between text-sm"
                        data-testid="summary-min-order-topup"
                      >
                        <span className="text-ink-3">
                          {tCo("summaryMinimumOrderTopup", {
                            defaultValue: "Dorovnání minimální objednávky",
                          })}
                        </span>
                        <span className="mono tabular-nums text-ink">
                          {moneyMinor(topupMinor)}
                        </span>
                      </div>
                      {minimumOrderMinor > 0 && (
                        <p
                          className="text-xs text-ink-4"
                          data-testid="summary-min-order-topup-hint"
                        >
                          {tCo("summaryMinimumOrderTopupHint", {
                            minimum: moneyMinor(minimumOrderMinor),
                            amount: moneyMinor(topupMinor),
                          })}
                        </p>
                      )}
                      </>
                      )}
                    </>
                  ) : null;
                // KAN-1783 — proč se DPH účtuje, když zákazník zadal DIČ:
                // VIES ho neověřil (nebo neodpověděl). Bez věty by zákazník
                // viděl 21 % a nevěděl, že jeho DIČ „nezabralo".
                // KAN-1868 — jen když by platné DIČ DPH opravdu změnilo
                // (rozhoduje server); u tuzemského B2B by věta lhala.
                const vatIdNote =
                  preview.vat_id_can_reverse_charge === true &&
                  (preview.vat_id_status === "invalid" ||
                    preview.vat_id_status === "unavailable") ? (
                    <p
                      className="text-xs text-ink-4"
                      data-testid="summary-vat-id-note"
                    >
                      {tCo(
                        preview.vat_id_status === "invalid"
                          ? "summaryVatIdInvalid"
                          : "summaryVatIdUnavailable",
                      )}
                    </p>
                  ) : null;
                // KAN-2100 — souhrn pojmenuje pravidlo, podle kterého se
                // sazba vybrala (důvod rozhoduje server). Bez věty majitel
                // ani zákazník neví, proč slovenská objednávka nese „21 % (CZ)".
                const vatRuleKey: Record<string, string> = {
                  home_country_rate_below_threshold: "summaryVatRuleHome",
                  oss_destination_rate: "summaryVatRuleOss",
                  b2b_taxed_in_dispatch_country: "summaryVatRuleB2bDispatch",
                  export_outside_eu: "summaryVatRuleExport",
                };
                const ruleKey = preview.vat_reason
                  ? vatRuleKey[preview.vat_reason]
                  : undefined;
                const vatRuleNote = ruleKey ? (
                  <p
                    className="text-xs text-ink-4"
                    data-testid="summary-vat-rule"
                  >
                    {tCo(ruleKey, { country: preview.vat_country_code ?? "" })}
                  </p>
                ) : null;
                if (!totals.hasVat) {
                  return (
                    <>
                      {topupRow}
                      {preview.vat_scheme === "reverse_charge_eu" && (
                        <div
                          className="text-sm text-ink-3"
                          data-testid="summary-reverse-charge"
                        >
                          {tCo("summaryReverseCharge")}
                        </div>
                      )}
                      {vatRuleNote}
                      {vatIdNote}
                      <div
                        className="flex items-baseline justify-between border-t border-border pt-3"
                        data-testid="summary-total"
                      >
                        <span className="text-sm text-ink-3">
                          {tCo("summaryTotal", {
                            defaultValue: "Celkem k úhradě",
                          })}
                        </span>
                        <span className="mono text-lg font-semibold tabular-nums text-ink">
                          {moneyMinor(grandTotalMinor)}
                        </span>
                      </div>
                    </>
                  );
                }
                return (
                  <>
                    {topupRow}
                    {/* Čistá cena je jen informativní řádek PŘED DPH —
                        „Celkem bez DPH" vedle nenulové DPH by vypadalo jako
                        částka k úhradě (KAN-1783). Úhrn je vždy s DPH. */}
                    {totals.showNetRow && (
                      <div
                        className="flex justify-between text-sm"
                        data-testid="summary-total-net"
                      >
                        <span className="text-ink-3">
                          {tCo("summaryTotalNet", {
                            defaultValue: "Celkem bez DPH",
                          })}
                        </span>
                        <span className="mono tabular-nums text-ink">
                          {moneyMinor(totals.netMinor)}
                        </span>
                      </div>
                    )}
                    <div
                      className="flex justify-between text-sm"
                      data-testid="summary-vat"
                    >
                      <span className="text-ink-3">
                        {tCo("summaryVat", {
                          defaultValue: "DPH {{rate}} % ({{country}})",
                          rate: formatVatRate(vatRate),
                          country: preview.vat_country_code ?? "",
                        })}
                      </span>
                      <span className="mono tabular-nums text-ink">
                        {moneyMinor(totalVatMinor)}
                      </span>
                    </div>
                    {vatRuleNote}
                    {vatIdNote}
                    <div
                      className="flex items-baseline justify-between border-t border-border pt-3"
                      data-testid="summary-total"
                    >
                      <span className="text-sm text-ink-3">
                        {tCo("summaryTotalGross", {
                          defaultValue: "Celkem k úhradě (s DPH)",
                        })}
                      </span>
                      <span className="mono text-lg font-semibold tabular-nums text-ink">
                        {moneyMinor(grandTotalMinor)}
                      </span>
                    </div>
                  </>
                );
              })()}
              {/* Jméno OBCHODU, ne platformy: osamocené „Forgmatic" pod souhrnem
                  tvrdilo, že objednávku přijímá platforma (KAN-1796). Bez
                  jména obchodu se řádek nekreslí. */}
              {branding.data?.name?.trim() ? (
                <div
                  className="border-t border-border pt-3 text-sm text-ink-3"
                  data-testid="summary-shop-name"
                >
                  {branding.data.name.trim()}
                </div>
              ) : null}
              {/*
                Distinct ``key`` props on each button so React's
                reconciler treats them as separate elements — see
                comment trail before 2026-04-26 for the bug this
                dodges. PR 5 swaps the visual styling but keeps the
                key dance + preventDefault belt.
              */}
              {step === "address" ? (
                <>
                  <Button
                    key="continue"
                    type="button"
                    variant="primary"
                    size="lg"
                    className="w-full"
                    disabled={
                      noCarriersConfigured ||
                      noDeliveryTiersConfigured ||
                      shopBlocksCheckout
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      if (
                        !guardContinue.check(
                          !shipping.first_name.trim() &&
                            tCo("validation.firstNameRequired", {
                              defaultValue: "Vyplňte jméno příjemce.",
                            }),
                          !shipping.last_name.trim() &&
                            tCo("validation.lastNameRequired", {
                              defaultValue: "Vyplňte příjmení příjemce.",
                            }),
                          !shipping.street.trim() &&
                            tCo("validation.streetRequired", {
                              defaultValue: "Vyplňte ulici a číslo popisné.",
                            }),
                          !shipping.city.trim() &&
                            tCo("validation.cityRequired", {
                              defaultValue: "Vyplňte město.",
                            }),
                          !shipping.postal_code.trim() &&
                            tCo("validation.postalCodeRequired", {
                              defaultValue: "Vyplňte PSČ.",
                            }),
                          !shipping.country_code.trim() &&
                            tCo("validation.countryRequired", {
                              defaultValue: "Vyberte zemi doručení.",
                            }),
                          shippingHints.postalInvalid &&
                            postalFormatMessage(shippingHints),
                          !billingSame &&
                            billingHints.postalInvalid &&
                            postalFormatMessage(billingHints),
                          !(shipping.phone ?? "").trim() &&
                            tCo("validation.phoneRequired", {
                              defaultValue: "Vyplňte telefon pro dopravce.",
                            }),
                          !(shipping.email ?? "").trim() &&
                            tCo("validation.emailRequired", {
                              defaultValue:
                                "Vyplňte e-mail pro potvrzení objednávky.",
                            }),
                          icoMissing && tCo("validation.icoRequiredB2b"),
                          icoInvalid && tCo("validation.invalidIco"),
                          dicInvalid && tCo("validation.invalidDic"),
                        )
                      ) {
                        return;
                      }
                      setStep("delivery");
                    }}
                    data-testid="continue-to-delivery"
                  >
                    {tCo("step.continue")}
                  </Button>
                  <SubmitBlockers blockers={guardContinue.blockers} />
                </>
              ) : (
                <>
                  {/* Souhlas s podmínkami a poučení o odstoupení. Odkazy
                      vedou na dokumenty PRODÁVAJÍCÍHO (shell scope), ne
                      platformy, a otevírají se v nové kartě — kliknutí
                      uprostřed pokladny nesmí zahodit vyplněný formulář. */}
                  <label
                    className="flex items-start gap-2 text-sm text-ink-2"
                    data-testid="terms-accept-label"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      data-testid="terms-accept"
                    />
                    <span>
                      <Trans
                        i18nKey={
                          businessOnly
                            ? "checkout:termsConsentB2b"
                            : "checkout:termsConsent"
                        }
                        defaults={
                          businessOnly
                            ? "Souhlasím s <terms>obchodními podmínkami</terms>."
                            : "Souhlasím s <terms>obchodními podmínkami</terms> a beru na vědomí <withdrawal>poučení o odstoupení od smlouvy</withdrawal>."
                        }
                        components={{
                          terms: (
                            <Link
                              to={`/${lang}/terms`}
                              target="_blank"
                              rel="noreferrer"
                              className="underline"
                            />
                          ),
                          withdrawal: (
                            <Link
                              to={`/${lang}/withdrawal`}
                              target="_blank"
                              rel="noreferrer"
                              className="underline"
                            />
                          ),
                        }}
                      />
                    </span>
                  </label>
                  {isTenantAdmin && (
                    <label
                      className="flex items-start gap-2 text-sm text-ink-2"
                      data-testid="test-order-label"
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={isTestOrder}
                        onChange={(e) => setIsTestOrder(e.target.checked)}
                        data-testid="test-order-checkbox"
                      />
                      <span>
                        {tCo("testOrderCheckbox", {
                          defaultValue:
                            "Testovací objednávka — nevstoupí do tržeb, provizí ani statistik",
                        })}
                      </span>
                    </label>
                  )}
                  {/* KAN-993 — volba se ukazuje jen tehdy, KDYŽ JE Z ČEHO
                      vybírat. Jediný způsob vykreslený jako „přepínač"
                      s jednou možností by předstíral rozhodnutí, které
                      zákazník nemá; obchod bez Stripe i obchod bez
                      povoleného převodu tak vypadá přesně jako dnes. */}
                  {offeredPaymentMethods.length > 1 && (
                    <fieldset
                      className="space-y-2"
                      data-testid="checkout-payment-method"
                    >
                      <legend className="text-sm font-medium text-ink">
                        {tCo("paymentMethod.legend")}
                      </legend>
                      {offeredPaymentMethods.map((method) => (
                        <label
                          key={method}
                          className="flex items-start gap-2 text-sm text-ink-2"
                          data-testid={`checkout-payment-method-${method}`}
                        >
                          <input
                            type="radio"
                            className="mt-0.5"
                            name="payment_method"
                            value={method}
                            checked={effectivePaymentMethod === method}
                            onChange={() => setPaymentMethod(method)}
                          />
                          <span>
                            <span className="text-ink">
                              {tCo(`paymentMethod.${method}`)}
                            </span>
                            <span className="block text-xs text-ink-3">
                              {tCo(
                                method === "card"
                                  ? "paymentMethod.cardHint"
                                  : method === "account"
                                    ? "paymentMethod.accountHint"
                                    : "paymentMethod.bankTransferHint",
                              )}
                            </span>
                          </span>
                        </label>
                      ))}
                    </fieldset>
                  )}
                  <Button
                    key="submit"
                    type="submit"
                    variant="accent"
                    size="lg"
                    className="w-full"
                    disabled={
                      cart.isLoading ||
                      !carrier ||
                      noCarriersConfigured ||
                      noDeliveryTiersConfigured ||
                      shopBlocksCheckout ||
                      // Bez souhlasu se objednávka neodešle. Server to
                      // ověřuje znovu (422 ``terms_not_accepted``) —
                      // tohle je jen to, aby uživatel nenarazil na chybu.
                      !termsAccepted ||
                      // Pod min. subtotal by server vrátil 422
                      // ``order_below_min_subtotal`` — vysvětlení visí
                      // hned vedle (KAN-739).
                      belowMinSubtotal
                    }
                    loading={checkout.isPending || demoEnd.isPending}
                    data-testid="place-order"
                  >
                    {checkout.isPending || demoEnd.isPending
                      ? tCo("placing")
                      : priceChange
                        ? tCo("placeOrderConfirmNewPrice")
                        : paysByTransfer
                          ? tCo("placeOrderTransfer")
                          : tCo("placeOrder")}
                  </Button>
                  {/* KAN-2189 — odeslání trvá i desítky sekund; samotné
                      kolečko vypadalo jako zaseknutá stránka. */}
                  {(checkout.isPending || demoEnd.isPending) && (
                    <p
                      className="text-center text-sm text-ink-2"
                      role="status"
                      data-testid="checkout-placing-status"
                    >
                      {tCo("placingSlow")}
                    </p>
                  )}
                </>
              )}
              {step === "delivery" && (
                <p
                  className="mono text-center text-[10.5px] uppercase tracking-[0.08em] text-ink-3"
                  data-testid="checkout-payment-note"
                >
                  {/* Card brands are a promise. A shop that cannot
                      charge a card must not make it - it says how the
                      order will actually be settled instead. */}
                  {/* KAN-2189 — „pokyny hned po odeslání" jen když server
                      převod nabídl (nabízí ho jen obchodu s číslem účtu,
                      KAN-2013). Firemní účet dostane fakturu se splatností
                      a dokud nabídka nedorazila, nic neslibujeme. */}
                  {effectivePaymentMethod === "bank_transfer"
                    ? tCo("paymentTransferRow")
                    : effectivePaymentMethod === "account"
                      ? tCo("paymentAccountRow")
                      : paysByTransfer
                        ? tCo("paymentTransferRowPlain")
                        : tCo("paymentTrustRow")}
                </p>
              )}
              {belowMinSubtotal && (
                <p
                  className="text-sm text-warn"
                  data-testid="checkout-min-subtotal-warning"
                >
                  {tCo("errors.orderBelowMinSubtotal", {
                    subtotal:
                      cartSubtotalMinor !== null
                        ? moneyMinor(cartSubtotalMinor)
                        : "",
                    min_subtotal:
                      minSubtotalMinor !== null
                        ? moneyMinor(minSubtotalMinor)
                        : "",
                  })}
                </p>
              )}
              {errorMessage && (
                <p
                  className="text-sm text-danger"
                  role="alert"
                  data-testid="checkout-error"
                >
                  {errorMessage}
                </p>
              )}
            </Card>
          </form>
        )}
      </main>
    </>
  );
}

// --- Sub-components --------------------------------------------------

/** Kam si zákazník pro zakázku přijde — pod vybírátkem, když zvolil
 *  osobní odběr. Adresa a hodiny jdou z Profilu firmy (KAN-1605). */
function PickupPlace({
  pickup,
  lang,
}: {
  pickup: CheckoutPickup;
  lang: string;
}): JSX.Element {
  const { t } = useTranslation("checkout");
  const hours =
    formatWeeklySchedule(pickup.location.schedule, lang) ??
    pickup.location.hours ??
    null;
  return (
    <dl
      className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm"
      data-testid="checkout-pickup-place"
    >
      <dt className="text-ink-3">{t("pickupAddressLabel")}</dt>
      <dd className={pickup.location.address ? "text-ink" : "text-ink-3"}>
        {pickup.location.address ?? t("pickupAddressMissing")}
      </dd>
      {hours && (
        <>
          <dt className="text-ink-3">{t("pickupHoursLabel")}</dt>
          <dd className="text-ink">{hours}</dd>
        </>
      )}
      <dd className="col-span-2 text-ink-3">{t("pickupReadyNote")}</dd>
    </dl>
  );
}

/**
 * KAN-2136 — apply only the fields an edit changed, on top of the CURRENT
 * state. The form builds ``next`` from the address it last rendered; a
 * default country (KAN-1928) that lands between that render and a
 * keystroke would otherwise be overwritten with the blank one — the
 * select keeps showing a country while the value is empty, and
 * "Pokračovat" stops on "Vyberte zemi doručení".
 */
function applyEdit(
  base: Address,
  next: Address,
): (prev: Address) => Address {
  return (prev) => {
    const out: Record<string, unknown> = { ...prev };
    const before = base as unknown as Record<string, unknown>;
    for (const [key, value] of Object.entries(next)) {
      if (value !== before[key]) out[key] = value;
    }
    return out as unknown as Address;
  };
}

/**
 * KAN-923 — copy ``src`` into the fields of ``prev`` that are still
 * blank. Typed-in values always win over the prefill.
 *
 * KAN-1913 — ``country_code`` is rarely blank (the form is given a
 * default country, KAN-1928), so "blank" cannot tell whether the
 * customer chose it. ``countryFree`` says the country is still the untouched default:
 * the prefilled address then brings its own country, otherwise a
 * Bratislava street ends up shipped and taxed as CZ.
 */
function fillEmptyFields(
  prev: Address,
  src: CheckoutPrefillAddress | null | undefined,
  countryFree = false,
): Address {
  if (!src) return prev;
  const next: Record<string, string | undefined> = { ...prev };
  for (const [key, value] of Object.entries(src)) {
    const free =
      !(next[key] ?? "").trim() || (key === "country_code" && countryFree);
    if (typeof value === "string" && value && free) {
      next[key] = value;
    }
  }
  return next as unknown as Address;
}

/** i18n key + count for a rung's lead time, shared by picker and info line. */
function ladderDaysLabel(
  opt: CartDeliveryLadderOption,
): [string, { count: number }] {
  const business = opt.day_basis === "business" && opt.days_business != null;
  return [
    business
      ? "deliveryBusinessDays"
      : opt.day_basis === "mixed"
        ? "deliveryMixedDays"
        : "deliveryCalendarDays", // KAN-1623 — ne „pracovní den"
    { count: business ? opt.days_business! : opt.days_calendar },
  ];
}

// KAN-925 — the "3D tisk: Standard · Tampon: Standard" and "3D tisk →
// standard" rows are noise when every operation resolved the same way;
// they only earn their place when the rung differs per operation.
function tiersDifferPerOperation(opt: CartDeliveryLadderOption): boolean {
  return new Set(opt.per_capacity.map((c) => c.code)).size > 1;
}

function rungsDifferPerOperation(opt: CartDeliveryLadderOption): boolean {
  if (opt.fallbacks.length === 0) return false;
  const fallen = new Set(opt.fallbacks.map((f) => f.process_type));
  const rungs = new Set(opt.fallbacks.map((f) => f.actual_ladder_code));
  if (opt.per_capacity.some((c) => !fallen.has(c.process_type))) {
    rungs.add(opt.ladder_code);
  }
  return rungs.size > 1;
}

interface AddressHints {
  postalInvalid: boolean;
  postalFirst: boolean;
  countryName: string;
  /** A postal code that fits the country's format, from the catalog. */
  postalExample: string | null;
}

/** "…for Česko, e.g. 110 00." — the example only when the catalog has one. */
function postalCodeFormatMessage(
  t: TFunction<"checkout">,
  hints: Pick<AddressHints, "countryName" | "postalExample">,
): string {
  return hints.postalExample
    ? t("validation.postalCodeFormatExample", {
        country: hints.countryName,
        example: hints.postalExample,
      })
    : t("validation.postalCodeFormat", { country: hints.countryName });
}

function AddressFields({
  value,
  onChange,
  heading,
  testPrefix,
  hints,
}: {
  value: Address;
  onChange: (next: Address) => void;
  heading: string;
  testPrefix: string;
  hints: AddressHints;
}): JSX.Element {
  const { t } = useTranslation("checkout");
  const update = (patch: Partial<Address>) => onChange({ ...value, ...patch });
  // KAN-923 — standard autofill section so the browser can offer its
  // own saved address too; ``shipping`` / ``billing`` per WHATWG.
  const section = testPrefix === "bill" ? "billing" : "shipping";

  const cityField = (
    <Field
      key="city"
      label={t("field.city")}
      value={value.city}
      onChange={(city) => update({ city })}
      required
      name={`${testPrefix}-city`}
      autoComplete={`${section} address-level2`}
    />
  );
  const postalField = (
    <div key="postal">
      <Field
        label={t("field.postalCode")}
        value={value.postal_code}
        onChange={(postal_code) => update({ postal_code })}
        required
        name={`${testPrefix}-postal`}
        autoComplete={`${section} postal-code`}
        invalid={hints.postalInvalid}
      />
      {hints.postalInvalid && (
        <p
          className="mt-1 text-sm text-danger"
          role="alert"
          data-testid={`${testPrefix}-postal-invalid`}
        >
          {postalCodeFormatMessage(t, hints)}
        </p>
      )}
    </div>
  );

  return (
    <Card padded elevation="flat">
      <h2 className="mb-3 text-lg font-semibold tracking-snug text-ink">
        {heading}
      </h2>
      <div className="grid gap-3 md:grid-cols-2">
        <Field
          label={t("field.firstName")}
          value={value.first_name}
          onChange={(first_name) => update({ first_name })}
          required
          name={`${testPrefix}-first-name`}
          autoComplete={`${section} given-name`}
        />
        <Field
          label={t("field.lastName")}
          value={value.last_name}
          onChange={(last_name) => update({ last_name })}
          required
          name={`${testPrefix}-last-name`}
          autoComplete={`${section} family-name`}
        />
        <Field
          label={t("field.company")}
          value={value.company ?? ""}
          onChange={(company) => update({ company })}
          wide
          name={`${testPrefix}-company`}
          autoComplete={`${section} organization`}
        />
        <Field
          label={t("field.street")}
          value={value.street}
          onChange={(street) => update({ street })}
          required
          wide
          name={`${testPrefix}-street`}
          autoComplete={`${section} street-address`}
        />
        {/* KAN-1456 — "110 00 Praha" vs "London SW1A 1AA": the pair follows
            the country's catalog address format. */}
        {hints.postalFirst ? [postalField, cityField] : [cityField, postalField]}
        <label className="block text-sm">
          <span className="mb-1 block text-ink">
            {t("field.countryCode")}
            <span className="text-danger"> *</span>
          </span>
          <CountrySelect
            value={value.country_code}
            onChange={(country_code) => update({ country_code })}
            required
            name={`${testPrefix}-country`}
            id={`${testPrefix}-country`}
            autoComplete={`${section} country`}
            testId={`${testPrefix}-country`}
            shippableOnly={section === "shipping"}
            className="block h-10 w-full rounded-md border border-border-strong bg-surface px-3 text-sm text-ink hover:border-ink-3 focus:outline-none"
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-ink">
            {t("field.phone")}
            <span className="text-danger"> *</span>
          </span>
          <PhoneInput
            value={value.phone ?? ""}
            onChange={(phone) => update({ phone })}
            linkedCountryCode={value.country_code}
            required
            name={`${testPrefix}-phone`}
            testId={`${testPrefix}-phone`}
          />
        </label>
        <Field
          label={t("field.email")}
          value={value.email ?? ""}
          onChange={(email) => update({ email })}
          required
          type="email"
          name={`${testPrefix}-email`}
          autoComplete={`${section} email`}
        />
      </div>
    </Card>
  );
}

function Field({
  label,
  value,
  onChange,
  required = false,
  wide = false,
  name,
  type = "text",
  autoComplete,
  invalid = false,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  required?: boolean;
  wide?: boolean;
  name?: string;
  type?: "text" | "email";
  autoComplete?: string;
  invalid?: boolean;
}): JSX.Element {
  return (
    <label className={cx("block text-sm", wide && "md:col-span-2")}>
      <span className="mb-1 block text-ink">
        {label}
        {required && <span className="text-danger"> *</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        // KAN-923 — ``name``/``id`` so the browser (and a11y tooling)
        // can tell the fields apart; without them autofill had nothing
        // to attach to.
        name={name}
        id={name}
        autoComplete={autoComplete}
        aria-invalid={invalid || undefined}
        data-testid={name}
        className={cx(
          "block h-10 w-full rounded-md border bg-surface px-3 text-sm text-ink hover:border-ink-3 focus:outline-none",
          invalid ? "border-danger-border" : "border-border-strong",
        )}
      />
    </label>
  );
}
